<script setup lang="ts">
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { useAppStore } from '@/core/stores/useAppStore'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { useThemeStore } from '@/core/stores/useThemeStore'
import { usePendingRequests } from '@/modules/event-reservations/composables/usePendingRequests'
import { useOrderNotifications } from '@/modules/orders/composables/useOrderNotifications'
import { eventReservationsApi, type MoveRequest, type CancellationRequest } from '@/modules/event-reservations/api/eventReservationsApi'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { logger } from '@/core/utils/logger'
import {
  IconBell,
  IconLogout,
  IconMoon,
  IconSun,
  IconHome,
  IconChevronRight,
  IconMenu2,
  IconX,
  IconCalendarEvent,
  IconShoppingCart,
  IconCircleCheck,
  IconCircleX,
  IconArrowRight,
} from '@tabler/icons-vue'
import LocaleSwitcher from '@/components/common/LocaleSwitcher.vue'
import AppSlideOver from '@/components/common/AppSlideOver.vue'
import AppButton from '@/components/common/AppButton.vue'
import AppTextarea from '@/components/common/AppTextarea.vue'
import AppSelect from '@/components/common/AppSelect.vue'
import AppInput from '@/components/common/AppInput.vue'

const auth = useAuthStore()
const appStore = useAppStore()
const projectsStore = useProjectsStore()
const themeStore = useThemeStore()
const route = useRoute()
const router = useRouter()
const pendingRequests = usePendingRequests()
const orderNotifications = useOrderNotifications()

// Notification panel state
const notifOpen = ref(false)
const notifTab = ref<'all' | 'requests' | 'orders'>('all')

// Approve/Reject modal state
const showApproveModal = ref(false)
const showRejectModal = ref(false)
const selectedRequest = ref<MoveRequest | CancellationRequest | null>(null)
const requestType = ref<'move' | 'cancellation'>('move')
const adminNote = ref('')
const refundType = ref('credit')
const refundAmount = ref('')

const refundTypeOptions = [
  { value: 'full', label: 'Plná refundace' },
  { value: 'partial', label: 'Částečná refundace' },
  { value: 'credit', label: 'Store kredit' },
  { value: 'none', label: 'Žádná refundace' }
]

// Breadcrumb from current route
const breadcrumb = computed(() => {
  const meta = route.meta as Record<string, string>
  return meta?.title || route.name?.toString() || ''
})

// Initialize pending requests monitoring
onMounted(() => {
  if (projectsStore.currentProject) {
    pendingRequests.fetchPendingCounts()
    pendingRequests.startAutoRefresh(10000)
    orderNotifications.fetchUnreadNotesCount()
    orderNotifications.startAutoRefresh(10000)
  }
})

onUnmounted(() => {
  pendingRequests.stopAutoRefresh()
  orderNotifications.stopAutoRefresh()
})

watch(() => projectsStore.currentProject, (newProject) => {
  if (newProject) {
    pendingRequests.fetchPendingCounts()
    orderNotifications.fetchUnreadNotesCount()
  }
})

const hasAnyNotification = computed(() =>
  pendingRequests.hasPendingRequests.value || orderNotifications.hasUnreadNotes.value
)

async function goToOrderAndMarkRead(orderId: string) {
  notifOpen.value = false
  await orderNotifications.markAsRead(orderId)
  router.push(`/orders/${orderId}/detail`)
}

function formatDate(dateString: string | undefined) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  if (dateString.includes('T')) {
    return date.toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function openApproveModal(request: MoveRequest | CancellationRequest, type: 'move' | 'cancellation') {
  selectedRequest.value = request
  requestType.value = type
  adminNote.value = ''
  refundType.value = 'credit'
  refundAmount.value = ''
  showApproveModal.value = true
}

function openRejectModal(request: MoveRequest | CancellationRequest, type: 'move' | 'cancellation') {
  selectedRequest.value = request
  requestType.value = type
  adminNote.value = ''
  showRejectModal.value = true
}

async function approveRequest() {
  if (!selectedRequest.value || !projectsStore.currentProject) return
  try {
    if (requestType.value === 'move') {
      await eventReservationsApi.approveMoveRequest(
        selectedRequest.value.id,
        projectsStore.currentProject.code,
        adminNote.value || undefined
      )
      appStore.addNotification('Žádost o přesun byla schválena', 'success')
    } else {
      await eventReservationsApi.approveCancellationRequest(
        selectedRequest.value.id,
        projectsStore.currentProject.code,
        {
          approvedRefundType: refundType.value,
          refundAmount: refundAmount.value || undefined,
          adminNote: adminNote.value || undefined
        }
      )
      appStore.addNotification('Žádost o zrušení byla schválena', 'success')
    }
    showApproveModal.value = false
    await pendingRequests.fetchPendingCounts()
  } catch (error: unknown) {
    appStore.addNotification('Nepodařilo se schválit žádost', 'error')
    logger.error('Failed to approve request:', error)
  }
}

async function rejectRequest() {
  if (!selectedRequest.value || !projectsStore.currentProject || !adminNote.value) return
  try {
    if (requestType.value === 'move') {
      await eventReservationsApi.rejectMoveRequest(
        selectedRequest.value.id,
        projectsStore.currentProject.code,
        adminNote.value
      )
      appStore.addNotification('Žádost o přesun byla zamítnuta', 'success')
    } else {
      await eventReservationsApi.rejectCancellationRequest(
        selectedRequest.value.id,
        projectsStore.currentProject.code,
        adminNote.value
      )
      appStore.addNotification('Žádost o zrušení byla zamítnuta', 'success')
    }
    showRejectModal.value = false
    await pendingRequests.fetchPendingCounts()
  } catch (error: unknown) {
    appStore.addNotification('Nepodařilo se zamítnout žádost', 'error')
    logger.error('Failed to reject request:', error)
  }
}
</script>

<template>
  <header class="h-[60px] min-h-[60px] bg-base-400 border-b border-white/[0.06] flex items-center px-4 sm:px-6 gap-4 z-30">
    <!-- Mobile hamburger -->
    <button
      class="md:hidden rail-btn !w-9 !h-9"
      @click="() => appStore.toggleMobileMenu()"
    >
      <IconMenu2 class="w-5 h-5" />
    </button>

    <!-- Breadcrumb -->
    <nav class="hidden sm:flex items-center gap-1.5 text-sm">
      <router-link to="/" class="text-gray-500 hover:text-gray-400 transition-colors">
        <IconHome class="w-4 h-4" />
      </router-link>
      <IconChevronRight v-if="breadcrumb" class="w-3 h-3 text-gray-600" />
      <span v-if="breadcrumb" class="text-gray-200 font-medium">{{ breadcrumb }}</span>
    </nav>

    <!-- Spacer -->
    <div class="flex-1"></div>

    <!-- Action buttons -->
    <div class="flex items-center gap-1">
      <!-- Notifications -->
      <button
        class="rail-btn !w-9 !h-9 relative"
        title="Notifikace"
        @click="notifOpen = !notifOpen"
      >
        <IconBell class="w-[18px] h-[18px]" />
        <span
          v-if="hasAnyNotification"
          class="absolute top-1.5 right-1.5 w-[7px] h-[7px] bg-red-500 rounded-full border-2 border-base-400"
        ></span>
      </button>

      <!-- Divider -->
      <div class="w-px h-5 bg-white/[0.06] mx-1.5"></div>

      <!-- Language Switcher -->
      <LocaleSwitcher />

      <!-- Theme toggle -->
      <button
        class="rail-btn !w-9 !h-9"
        :title="themeStore.isDark ? 'Světlý režim' : 'Tmavý režim'"
        @click="themeStore.setTheme(themeStore.isDark ? 'light' : 'dark')"
      >
        <IconSun v-if="themeStore.isDark" class="w-[18px] h-[18px]" />
        <IconMoon v-else class="w-[18px] h-[18px]" />
      </button>

      <!-- Divider -->
      <div class="w-px h-5 bg-white/[0.06] mx-1.5"></div>

      <!-- Logout -->
      <button
        class="rail-btn !w-9 !h-9 hover:!text-red-400"
        title="Odhlásit se"
        @click="auth.logout"
      >
        <IconLogout class="w-[18px] h-[18px]" />
      </button>
    </div>
  </header>

  <!-- Notification Center — slides in from right -->
  <transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="notifOpen" class="fixed inset-0 bg-black/40 z-40" @click="notifOpen = false"></div>
  </transition>

  <transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="translate-x-full opacity-0"
    enter-to-class="translate-x-0 opacity-100"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="translate-x-0 opacity-100"
    leave-to-class="translate-x-full opacity-0"
  >
    <div
      v-if="notifOpen"
      class="fixed top-0 right-0 w-[420px] max-w-[100vw] h-full bg-base-400 border-l border-white/[0.06] z-50 flex flex-col shadow-[-8px_0_40px_rgba(0,0,0,0.4)]"
    >
      <!-- Header -->
      <div class="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <h3 class="text-[15px] font-semibold text-gray-200">Notifikace</h3>
        <button class="rail-btn !w-8 !h-8" @click="notifOpen = false">
          <IconX class="w-4 h-4" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex overflow-x-auto border-b border-white/[0.06] px-5 gap-1">
        <button
          v-for="tab in ([
            { key: 'all', label: 'Vše' },
            { key: 'requests', label: 'Požadavky' },
            { key: 'orders', label: 'Objednávky' },
          ] as const)"
          :key="tab.key"
          class="flex-shrink-0 py-2.5 px-3 text-xs font-semibold uppercase tracking-wide text-center -mb-px transition-colors whitespace-nowrap"
          :class="notifTab === tab.key
            ? 'text-amber-400 border-b-2 border-amber-400'
            : 'text-gray-500 border-b-2 border-transparent hover:text-gray-400'"
          @click="notifTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- === PENDING REQUESTS (move + cancellation) === -->
        <template v-if="notifTab === 'all' || notifTab === 'requests'">
          <!-- Move Requests -->
          <div
            v-for="request in pendingRequests.moveRequests.value"
            :key="'move-' + request.id"
            class="px-5 py-4 border-b border-white/[0.06] hover:bg-amber-400/[0.02] transition-colors"
          >
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded-md bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconCalendarEvent class="w-[18px] h-[18px] text-blue-400" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-sm font-medium text-gray-200">Žádost o přesun</p>
                  <span class="tag text-[10px] bg-blue-500/10 text-blue-400">přesun</span>
                </div>
                <p class="text-sm text-gray-300 mt-1">{{ request.customerName }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ request.productTitle }}</p>
                <div class="flex items-center gap-1.5 text-xs text-gray-500 mt-1.5">
                  <span>{{ formatDate(request.fromEventDateDisplay) }}</span>
                  <span v-if="request.fromSelectedTimeSlot" class="text-gray-400">{{ request.fromSelectedTimeSlot }}</span>
                  <IconArrowRight class="w-3 h-3" />
                  <span>{{ formatDate(request.toEventDateDisplay) }}</span>
                  <span v-if="request.selectedTimeSlot" class="text-blue-400">{{ request.selectedTimeSlot }}</span>
                </div>
                <p v-if="request.reason" class="text-xs text-gray-400 mt-1 line-clamp-2 italic">
                  „{{ request.reason }}"
                </p>
                <div class="flex gap-2 mt-3">
                  <AppButton size="sm" variant="success" @click="openApproveModal(request, 'move')">
                    <template #icon><IconCircleCheck class="w-3.5 h-3.5" /></template>
                    Schválit
                  </AppButton>
                  <AppButton size="sm" variant="danger" @click="openRejectModal(request, 'move')">
                    <template #icon><IconCircleX class="w-3.5 h-3.5" /></template>
                    Zamítnout
                  </AppButton>
                </div>
              </div>
            </div>
          </div>

          <!-- Cancellation Requests -->
          <div
            v-for="request in pendingRequests.cancellationRequests.value"
            :key="'cancel-' + request.id"
            class="px-5 py-4 border-b border-white/[0.06] hover:bg-amber-400/[0.02] transition-colors"
          >
            <div class="flex gap-3">
              <div class="w-9 h-9 rounded-md bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <IconCircleX class="w-[18px] h-[18px] text-red-400" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2">
                  <p class="text-sm font-medium text-gray-200">Žádost o zrušení</p>
                  <span class="tag text-[10px] bg-red-500/10 text-red-400">zrušení</span>
                </div>
                <p class="text-sm text-gray-300 mt-1">{{ request.customerName }}</p>
                <p class="text-xs text-gray-500 mt-0.5">{{ request.productTitle }}</p>
                <p class="text-xs text-gray-500 mt-0.5">Termín: {{ formatDate(request.eventDateDisplay) }}</p>
                <p v-if="request.requestedRefundType" class="text-xs text-gray-500 mt-0.5">
                  Požadovaná refundace: {{ request.requestedRefundType }}
                </p>
                <p v-if="request.reason" class="text-xs text-gray-400 mt-1 line-clamp-2 italic">
                  „{{ request.reason }}"
                </p>
                <div class="flex gap-2 mt-3">
                  <AppButton size="sm" variant="success" @click="openApproveModal(request, 'cancellation')">
                    <template #icon><IconCircleCheck class="w-3.5 h-3.5" /></template>
                    Schválit
                  </AppButton>
                  <AppButton size="sm" variant="danger" @click="openRejectModal(request, 'cancellation')">
                    <template #icon><IconCircleX class="w-3.5 h-3.5" /></template>
                    Zamítnout
                  </AppButton>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- === ORDER NOTES === -->
        <template v-if="notifTab === 'all' || notifTab === 'orders'">
          <div
            v-for="order in orderNotifications.ordersWithUnreadNotes.value"
            :key="order.id"
            class="flex gap-3 px-5 py-3.5 cursor-pointer hover:bg-amber-400/[0.03] transition-colors border-b border-white/[0.06]"
            @click="goToOrderAndMarkRead(order.id)"
          >
            <div class="w-9 h-9 rounded-md bg-amber-400/10 flex items-center justify-center flex-shrink-0">
              <IconShoppingCart class="w-[18px] h-[18px] text-amber-400" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <p class="text-sm font-medium text-gray-200 truncate">
                  {{ order.customerName || order.customerEmail }}
                </p>
                <span
                  class="tag text-[10px]"
                  :class="{
                    'bg-amber-400/10 text-amber-400': order.status === 'new',
                    'bg-green-500/10 text-green-400': order.status === 'paid',
                    'bg-blue-500/10 text-blue-400': order.status === 'completed',
                    'bg-red-500/10 text-red-400': order.status === 'cancelled',
                  }"
                >
                  {{ order.status }}
                </span>
              </div>
              <p class="text-xs text-gray-500">
                VS: {{ order.variableSymbol || '-' }} &bull; {{ order.totalPrice }} {{ order.currency }}
              </p>
              <p class="mt-1 text-sm text-amber-400/70 line-clamp-2">
                {{ order.notes }}
              </p>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-if="(notifTab === 'requests' && !pendingRequests.hasPendingRequests.value)
            || (notifTab === 'orders' && orderNotifications.ordersWithUnreadNotes.value.length === 0)
            || (notifTab === 'all' && !pendingRequests.hasPendingRequests.value && orderNotifications.ordersWithUnreadNotes.value.length === 0)"
          class="px-5 py-12 text-center text-gray-500"
        >
          <IconBell class="w-10 h-10 mx-auto mb-2 opacity-20" />
          <p class="text-sm">Žádné nové notifikace</p>
        </div>
      </div>
    </div>
  </transition>

  <!-- Approve Modal -->
  <AppSlideOver
    v-model="showApproveModal"
    :title="requestType === 'move' ? 'Schválit žádost o přesun' : 'Schválit žádost o zrušení'"
    width="md"
  >
    <div class="space-y-4">
      <div v-if="requestType === 'cancellation'">
        <AppSelect
          v-model="refundType"
          label="Typ refundace"
          :options="refundTypeOptions"
          required
        />
      </div>

      <div v-if="requestType === 'cancellation' && (refundType === 'partial' || refundType === 'full' || refundType === 'credit')">
        <AppInput
          v-model="refundAmount"
          label="Částka refundace"
          type="number"
          step="0.01"
          min="0"
          placeholder="např. 1500.00"
          helper-text="Částka v CZK"
        />
      </div>

      <AppTextarea
        v-model="adminNote"
        label="Poznámka administrátora (volitelné)"
        :rows="3"
        placeholder="Např. důvod schválení, dodatečné informace..."
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <AppButton variant="gray" @click="showApproveModal = false">
          Zrušit
        </AppButton>
        <AppButton variant="success" @click="approveRequest">
          <IconCircleCheck class="h-4 w-4 mr-1" />
          Schválit
        </AppButton>
      </div>
    </template>
  </AppSlideOver>

  <!-- Reject Modal -->
  <AppSlideOver
    v-model="showRejectModal"
    :title="requestType === 'move' ? 'Zamítnout žádost o přesun' : 'Zamítnout žádost o zrušení'"
    width="md"
  >
    <div class="space-y-4">
      <AppTextarea
        v-model="adminNote"
        label="Důvod zamítnutí (povinné)"
        :rows="3"
        required
        placeholder="Např. termín je již plný, deadline vypršel..."
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <AppButton variant="gray" @click="showRejectModal = false">
          Zrušit
        </AppButton>
        <AppButton
          variant="danger"
          :disabled="!adminNote"
          @click="rejectRequest"
        >
          <IconCircleX class="h-4 w-4 mr-1" />
          Zamítnout
        </AppButton>
      </div>
    </template>
  </AppSlideOver>
</template>
